"use client";

import { usePortfolioBuilderStore } from "@/stores/portfolioBuilderStore";
import { useRecommendation } from "@/hooks/usePortfolioBuilder";
import type { RiskTolerance } from "@/types/portfolio-builder.types";

const LEVERAGE_MAP: Record<RiskTolerance, number> = {
  Conservative: 1.0,
  Moderate: 1.5,
  "A Bit Aggressive": 2.0,
  Aggressive: 3.0,
};

const RISK_OPTIONS: Array<{
  value: RiskTolerance;
  label: string;
  desc: string;
  color: string;
}> = [
  {
    value: "Conservative",
    label: "Conservative",
    desc: "Max 5% drawdown, steady returns",
    color: "border-green-600/50 bg-green-900/10",
  },
  {
    value: "Moderate",
    label: "Moderate",
    desc: "Max 10% drawdown, balanced growth",
    color: "border-blue-600/50 bg-blue-900/10",
  },
  {
    value: "A Bit Aggressive",
    label: "A Bit Aggressive",
    desc: "Max 20% drawdown, higher returns",
    color: "border-amber-600/50 bg-amber-900/10",
  },
  {
    value: "Aggressive",
    label: "Aggressive",
    desc: "Max 25% drawdown, maximum growth",
    color: "border-red-600/50 bg-red-900/10",
  },
];

function formatCurrency(n: number): string {
  return "$" + n.toLocaleString("en-US");
}

export function GoalsStep() {
  const store = usePortfolioBuilderStore();
  const { mutate: recommend, isPending } = useRecommendation();

  const leverage = LEVERAGE_MAP[store.riskTolerance];
  const totalAllocation = store.sovrunAllocation * leverage;

  const handleSubmit = () => {
    const profile = store.getProfile();
    if (!profile) return;
    recommend(profile);
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-lg font-semibold">Investment Goals</h2>
        <p className="mt-1 text-sm text-gray-400">
          Set your risk tolerance and return target.
        </p>
      </div>

      {/* Risk tolerance radio cards */}
      <div className="space-y-2">
        <label className="text-sm font-medium text-gray-300">Risk Tolerance</label>
        <div className="grid grid-cols-2 gap-3">
          {RISK_OPTIONS.map(({ value, label, desc, color }) => (
            <button
              key={value}
              onClick={() => store.setRiskTolerance(value)}
              className={`rounded-lg border p-3 text-left transition-all ${
                store.riskTolerance === value
                  ? `${color} ring-1 ring-blue-500`
                  : "border-gray-800 bg-gray-900/50 hover:border-gray-700"
              }`}
            >
              <span className="text-sm font-medium">{label}</span>
              <p className="mt-0.5 text-xs text-gray-500">{desc}</p>
            </button>
          ))}
        </div>
      </div>

      {/* Target return slider */}
      <div className="rounded-lg border border-gray-800 bg-gray-900/50 p-4">
        <div className="mb-2 flex items-center justify-between">
          <span className="font-medium">Target Annual Return</span>
          <span className="text-sm font-medium text-blue-400">
            {(store.targetReturn * 100).toFixed(0)}%
          </span>
        </div>
        <input
          type="range"
          min={0.05}
          max={0.5}
          step={0.01}
          value={store.targetReturn}
          onChange={(e) => store.setTargetReturn(Number(e.target.value))}
          className="w-full accent-blue-500"
        />
        <div className="mt-1 flex justify-between text-xs text-gray-600">
          <span>5%</span>
          <span>50%</span>
        </div>
      </div>

      {/* Derived values summary */}
      <div className="rounded-lg border border-gray-800 bg-gray-900/50 p-4 space-y-2">
        <div className="flex items-center justify-between text-sm">
          <span className="text-gray-400">Leverage Factor</span>
          <span className="font-medium">{leverage}x</span>
        </div>
        <div className="flex items-center justify-between text-sm">
          <span className="text-gray-400">Total Allocation (with leverage)</span>
          <span className="font-medium text-blue-400">
            {formatCurrency(totalAllocation)}
          </span>
        </div>
      </div>

      {store.error && (
        <p className="rounded-md border border-red-800/50 bg-red-900/20 p-3 text-sm text-red-400">
          {store.error}
        </p>
      )}

      <div className="flex justify-between">
        <button
          onClick={() => store.prevStep()}
          className="rounded-lg border border-gray-700 px-6 py-2.5 text-sm text-gray-300 hover:bg-gray-800 transition-colors"
        >
          Back
        </button>
        <button
          onClick={handleSubmit}
          disabled={isPending || store.isLoading || !store.getProfile()}
          className="rounded-lg bg-blue-600 px-6 py-2.5 text-sm font-medium hover:bg-blue-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isPending || store.isLoading ? (
            <span className="flex items-center gap-2">
              <span className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
              Analyzing...
            </span>
          ) : (
            "Get Recommendation"
          )}
        </button>
      </div>
    </div>
  );
}
